/**
 * ResortBand — P3's desktop resort overview (CD-1).
 *
 * Renders at `lg+` only, full-rail, above the masonry columns. It ends the
 * "doorway card" compression: a 116-suite resort's entire operational picture
 * was a 437px tile saying "Portfolio Dashboard · 6 meters" with the actual
 * dashboard one click away, so nothing about the property was legible until you
 * left home. The band promotes the pieces `/hotel/portfolio` has already proven
 * — projected month total, occupancy, demand headroom, the four KPIs — and
 * keeps the full dashboard as the drill-in for meter-level work.
 *
 * Surface choice: `card-accent` rather than the P4 band's `card-dark`. Each
 * band inherits its persona's established surface — P3's hotel cards have been
 * brand-secondary since B1, P4's portfolio has been navy — so a persona's home
 * still reads as one design rather than as a shared component bolted on.
 *
 * Web-only by design (commercial is desktop-by-design per the locked boundary);
 * every figure arrives as a prop, resolved by the caller from the shared
 * `useBillState` and the persona fixture.
 *
 * Atlas: B1, I1.
 */
import Link from "next/link";
import { Moon, AlertTriangle, Zap, Leaf, BarChart2 } from "lucide-react";
import type { Persona } from "@petci/fixtures";
import { formatCurrency } from "@/lib/format";
import { useT } from "@/lib/locale";
import { KpiTile } from "@/components/KpiTile";

interface Props {
  kpi: NonNullable<Persona["hotelKpi"]>;
  /** The month's bill total — the same figure `/hotel/portfolio` calls projected. */
  projectedTotal: number;
}

export function ResortBand({ kpi, projectedTotal }: Props) {
  const t = useT();

  return (
    <section
      aria-labelledby="resort-band-title"
      className="hidden lg:block card-accent border-brand-secondary/20 space-y-5"
    >
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="min-w-0">
          <h2
            id="resort-band-title"
            className="text-xs font-semibold text-brand-secondary uppercase tracking-wider"
          >
            {t("home.portfolioDashboard")}
          </h2>
          {/* "Projected", not "balance": this is what the resort has consumed
              this month to date. The balance card in the columns below carries
              what is owed, and the two answer different questions — the same
              split `/hotel/portfolio` has always made. */}
          <p className="flex items-baseline gap-2 mt-1 flex-wrap">
            <span className="text-4xl font-display font-bold text-neutral-900 tabular-nums">
              {formatCurrency(projectedTotal)}
            </span>
            <span className="text-sm text-neutral-500">{t("hotel.portfolio.projected")}</span>
          </p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Link
              href="/hotel/ecor"
              /* F-5 paint-vs-hit, same treatment as the drill-in's pill. Worth
                 noting why this one was never in an audit list: e6's tap-target
                 pass runs at 390px, and the band only renders at `lg+`, so no
                 measurement has ever reached it. Same control, same 20px paint,
                 fixed by inspection rather than by report. */
              className="group inline-flex items-center py-1 -my-1"
            >
              {/* No aria-label override: WCAG 2.5.3 label-in-name wants the
                  accessible name to contain the visible text. The drill-in's
                  `occupancyAria` value ends "drag the slider on the ECOR page",
                  which both broke containment AND told AT users to perform the
                  very drag the F-3 fix removed from this control. The visible
                  content IS the name now; the Moon icon is aria-hidden, so the
                  name resolves to exactly "76% occupancy". */}
              <span className="badge bg-brand-secondary/10 text-brand-secondary group-hover:bg-brand-secondary/20 transition-colors duration-fast">
                <Moon size={11} aria-hidden />
                {/* F-3: band-scoped rather than the drill-in's shared key, whose
                    value ends "· drag →". The band links to the ECOR page; it has
                    no slider to drag, so the verb was an affordance the control
                    cannot honour. The two surfaces legitimately need different
                    values here — that is context, not drift. */}
                {t("hotel.band.occupancy", { pct: kpi.occupancyPct })}
              </span>
            </Link>
            <span className="badge bg-semantic-warning/10 text-semantic-warning-text">
              <AlertTriangle size={11} aria-hidden />
              {t("hotel.portfolio.nearPeakDemand")}
            </span>
          </div>
        </div>
        <Link href="/hotel/portfolio" className="btn-secondary flex-shrink-0">
          {t("home.openPortfolio")}
        </Link>
      </div>

      {/* The drill-in's KPI strip, unchanged in composition so the two surfaces
          cannot drift — same tiles, same order, same tokens. */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiTile
          icon={<Moon size={18} aria-hidden />}
          label={t("hotel.kpi.ecor")}
          value={formatCurrency(kpi.ecorUSD)}
          sub={t("hotel.kpi.ecorSub")}
          href="/hotel/ecor"
          colour="bg-brand-secondary/10 text-brand-secondary"
        />
        <KpiTile
          icon={<Zap size={18} aria-hidden />}
          label={t("hotel.kpi.peakDemand")}
          value={`${kpi.peakDemandKW} kW`}
          sub={t("hotel.kpi.peakDemandSub", { kw: kpi.peakDemandThresholdKW })}
          colour="bg-semantic-warning/10 text-semantic-warning-text"
        />
        <KpiTile
          icon={<Leaf size={18} aria-hidden />}
          label={t("hotel.kpi.pctRenewable")}
          value={`${kpi.pctRenewable}%`}
          sub={t("hotel.kpi.pctRenewableSub", { pct: kpi.industryBenchmarks.pctRenewable })}
          colour="bg-semantic-success/10 text-semantic-success-text"
        />
        <KpiTile
          icon={<BarChart2 size={18} aria-hidden />}
          label={t("hotel.kpi.kwhPerGuestNight")}
          value={`${kpi.kWhPerGuestNight}`}
          sub={t("hotel.kpi.kwhPerGuestNightSub", { value: kpi.industryBenchmarks.kWhPerGuestNight })}
          colour="bg-brand-primary/10 text-brand-primary-text"
        />
      </div>
    </section>
  );
}
